import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface ProductDescription {
  id: number;
  name: string;
  description: string;
  language_code: string;
  language_name: string;
}

interface Language {
  id: number;
  code: string;
  name: string;
}

interface Product {
  id: number;
  name: string;
}

interface ProductImagesMap {
  [productName: string]: string | null;
}

interface LocationState {
  descriptions: ProductDescription[];
  language: Language;
  requestedProducts: Product[];
}

const ResponseView: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const [copiedStates, setCopiedStates] = useState<{ [key: number]: boolean }>({});
  const [expandedStates, setExpandedStates] = useState<{ [key: number]: boolean }>({});
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [languageIntro, setLanguageIntro] = useState<string>('');
  const [productImages, setProductImages] = useState<ProductImagesMap>({});
  const [isLoadingImages, setIsLoadingImages] = useState(false);
  const [enlargedImage, setEnlargedImage] = useState<string | null>(null);
  
  const state = location.state as LocationState;

  // Compute language code safely for hooks usage
  const languageCode = state?.language?.code || '';

  // Load language intro for the selected language (hook must be before any early return)
  useEffect(() => {
    if (!languageCode) {
      setLanguageIntro('');
      return;
    }
    let aborted = false;
    const loadIntro = async () => {
      try {
        const res = await fetch(`/api/language-intros/${languageCode}`);
        if (res.ok) {
          const data = await res.json();
          if (!aborted) setLanguageIntro(data?.data?.content || '');
        } else if (!aborted) {
          setLanguageIntro('');
        }
      } catch (e) {
        if (!aborted) setLanguageIntro('');
      }
    };
    loadIntro();
    return () => {
      aborted = true;
    };
  }, [languageCode]);

  // Load product images from Buglo API
  useEffect(() => {
    if (!state?.descriptions || state.descriptions.length === 0) {
      return;
    }
    let aborted = false;
    const loadImages = async () => {
      setIsLoadingImages(true);
      try {
        const productNames = state.descriptions.map(d => d.name);
        const res = await fetch('/api/products/images', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productNames })
        });
        if (res.ok) {
          const data = await res.json();
          if (!aborted) setProductImages(data.data || {});
        }
      } catch (e) {
        console.error('Failed to load product images:', e);
      } finally {
        if (!aborted) setIsLoadingImages(false);
      }
    };
    loadImages();
    return () => {
      aborted = true;
    };
  }, [state?.descriptions]);

  if (!state || !state.descriptions) {
    return (
      <section className="results-section">
        <div className="results-container">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <h2>{t('noData')}</h2>
            <p>{t('noDataMessage')}</p>
            <button onClick={() => navigate('/')} className="submit-button">
              {t('backToFormButton')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  const { descriptions, language, requestedProducts } = state;

  const copyToClipboard = async (text: string, productId: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [productId]: true }));
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [productId]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const downloadImage = (productName: string) => {
    const imgUrl = productImages[productName];
    if (!imgUrl) return;
    const filename = `${productName}.jpg`;
    const proxyUrl = `/api/products/image-proxy?url=${encodeURIComponent(imgUrl)}&filename=${encodeURIComponent(filename)}`;
    const a = document.createElement('a');
    a.href = proxyUrl;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const generatePDF = async () => {
    try {
      setIsGeneratingPdf(true);
      
      // Przygotuj dane do wysłania
      const productIds = descriptions.map(desc => desc.id);
      const pdfPageLabels: Record<string, string> = {
        pl: 'Strona',
        en: 'Page',
        de: 'Seite',
        es: 'Página',
        fr: 'Page',
        it: 'Pagina',
        fi: 'Sivu',
        lv: 'Lapa',
        et: 'Lehekülg',
        lt: 'Puslapis',
        sk: 'Strana',
        ua: 'Сторінка',
        uk: 'Сторінка',
      };

      // Stałe teksty PDF z aktualnego języka UI (nie z backendowej mapy)
      const uiLabels = {
        title: t('pdfContentTitle'),
        languageLabel: t('pdfLanguageLabel'),
        generatedLabel: t('pdfGeneratedLabel'),
        productsCountLabel: t('pdfProductsCountLabel'),
        languageDescLabel: t('pdfLanguageDescLabel'),
        generationDateLabel: t('pdfGenerationDateLabel'),
        productIdLabel: t('pdfProductIdLabel'),
        productSeriesLabel: t('pdfProductSeriesLabel'),
        productPositionLabel: t('pdfProductPositionLabel'),
        noDescriptionMessage: t('pdfNoDescriptionMessage'),
        footerGenerated: t('pdfFooterGenerated'),
        footerTotal: t('pdfFooterTotal'),
        footerDate: t('pdfFooterDate'),
        page: pdfPageLabels[currentLanguage] || 'Strona',
      };
      
      const response = await fetch('/api/products/generate-pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          productIds: productIds,
          language: language.code,
          uiLanguage: currentLanguage,
          uiLabels,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 500 && errorData.message?.includes('temporarily unavailable')) {
          throw new Error('PDF generation is temporarily unavailable on this hosting environment. Please copy the text content or contact support for export options.');
        }
        throw new Error(errorData.message || 'Failed to generate PDF');
      }

      // Pobierz PDF jako blob
      const blob = await response.blob();
      
      // Utwórz URL do pobrania
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Utwórz nazwę pliku
      const filename = `opisy_produktow_${language.code}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.download = filename;
      
      // Kliknij link aby pobrać
      document.body.appendChild(link);
      link.click();
      
      // Wyczyść
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(t('error'));
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const handleNewSearch = () => {
    navigate('/');
  };

  // Function to check if text is too long (roughly 400px = ~600 characters)
  const isTextTooLong = (text: string): boolean => {
    return text.length > 400;
  };

  const toggleExpanded = (productId: number) => {
    setExpandedStates(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Sprawdź, czy wszystkie produkty mają opisy
  const productsWithoutDescriptions = requestedProducts.filter(
    requestedProduct => !descriptions.find(desc => desc.id === requestedProduct.id)
  );

  return (
    <section className="results-section">
      <div className="results-container">
        <h1 className="results-title">{t('resultsTitle')}</h1>
        
        {/* Produkty z opisami */}
        {descriptions.length > 0 && (
          <>
            {Boolean(languageIntro.trim()) && (
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                className={`general-intro-card ${descriptions.length === 1 ? 'col-1' : descriptions.length === 2 ? 'col-2' : 'col-3'}`}
              >
                <div className="product-card">
                  <h3 className="product-name">{t('resultsTitle')} – {language.name}</h3>
                  <div className="product-description expanded">
                    <p>{languageIntro}</p>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(languageIntro, -1)}
                  className="copy-button"
                  style={{ alignSelf: 'center' }}
                >
                  {copiedStates[-1] ? t('copiedButton') : t('copyButton')}
                </button>
              </div>
            )}
            <div className="products-grid">
              {descriptions.map((productDesc) => {
                const isExpanded = expandedStates[productDesc.id];
                const isTooLong = isTextTooLong(productDesc.description || '');
                const shouldTruncate = isTooLong && !isExpanded;
                
                return (
                  <div key={productDesc.id} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <div className="product-card">
                      <h3 className="product-name">{productDesc.name}</h3>
                      
                      <div className={`product-description ${(!isTooLong || isExpanded) ? 'expanded' : ''}`}>
                        {productDesc.description ? (
                          <p style={{
                            maxHeight: shouldTruncate ? '200px' : 'none',
                            overflow: shouldTruncate ? 'hidden' : 'visible',
                            position: 'relative'
                          }}>
                            {productDesc.description}
                          </p>
                        ) : (
                          <p style={{ color: '#666', fontStyle: 'italic' }}>
                            {t('noDescription')}
                          </p>
                        )}
                      </div>
                      
                      {/* Przycisk rozwijania poza opisem */}
                      {isTooLong && productDesc.description && (
                        <button
                          onClick={() => toggleExpanded(productDesc.id)}
                          className="expand-button"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#F44603',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            padding: '8px 0',
                            textAlign: 'left',
                            alignSelf: 'flex-start'
                          }}
                        >
                          {isExpanded ? t('collapseText') : t('expandText')}
                        </button>
                      )}

                      {/* Miniaturka zdjęcia produktu */}
                      {productImages[productDesc.name] && (
                        <div style={{
                          marginTop: '16px',
                          paddingTop: '16px',
                          borderTop: '1px solid #e0e0e0',
                          textAlign: 'center'
                        }}>
                          <img 
                            src={productImages[productDesc.name]!} 
                            alt={productDesc.name}
                            onClick={() => setEnlargedImage(productImages[productDesc.name]!)}
                            style={{
                              maxWidth: '200px',
                              maxHeight: '200px',
                              cursor: 'pointer',
                              borderRadius: '8px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                              transition: 'transform 0.2s',
                              objectFit: 'contain'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                          />
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                      <button
                        onClick={() => copyToClipboard(productDesc.description || '', productDesc.id)}
                        className="copy-button"
                        disabled={!productDesc.description}
                      >
                        {copiedStates[productDesc.id] ? t('copiedButton') : t('copyButton')}
                      </button>
                      <button
                        onClick={() => downloadImage(productDesc.name)}
                        className="copy-button"
                        disabled={!productImages[productDesc.name]}
                        title={productImages[productDesc.name] ? '' : t('noDescription')}
                      >
                        {t('downloadButton')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* PDF Section */}
        {descriptions.length > 0 && (
          <div className="pdf-section">
            <div className="pdf-icon">
                <img src={require('../assets/images/pdf.svg').default} alt="PDF Icon" />
            </div>
            <h3 className="pdf-title">{t('pdfTitle')}</h3>
            
            <button 
              onClick={generatePDF} 
              className="pdf-button"
              disabled={isGeneratingPdf}
            >
              {isGeneratingPdf ? (
                <div className="loading">
                  <div className="spinner"></div>
                  {t('pdfGenerating')}
                </div>
              ) : (
                t('pdfButton')
              )}
            </button>
          </div>
        )}

        {/* Back to Form Button */}
        <div style={{ textAlign: 'center', marginTop: '40px' }}>
          <button 
            onClick={handleNewSearch}
            className="return-button"
          >
            {t('backToForm')}
          </button>
        </div>

        {/* Produkty bez opisów - jeśli są */}
        {productsWithoutDescriptions.length > 0 && (
          <div style={{ 
            marginTop: '40px', 
            padding: '20px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '8px',
            border: '1px solid #ffeaa7'
          }}>
            <h4 style={{ color: '#856404', marginBottom: '12px' }}>
              Produkty bez opisów w języku {language.name}:
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {productsWithoutDescriptions.map((product) => (
                <span 
                  key={product.id} 
                  style={{ 
                    backgroundColor: '#fff', 
                    padding: '4px 8px', 
                    borderRadius: '4px',
                    border: '1px solid #ffeaa7',
                    fontSize: '14px'
                  }}
                >
                  {product.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Modal dla powiększonego zdjęcia */}
        {enlargedImage && (
          <div 
            onClick={() => setEnlargedImage(null)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
              cursor: 'pointer',
              padding: '20px'
            }}
          >
            <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
              <img 
                src={enlargedImage} 
                alt="Powiększone zdjęcie"
                style={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setEnlargedImage(null);
                }}
                style={{
                  position: 'absolute',
                  top: '10px',
                  right: '10px',
                  background: 'rgba(0, 0, 0, 0.6)',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.8)'}
                onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0, 0, 0, 0.6)'}
              >
                ×
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ResponseView;
